<template lang="html">
  <v-flex
    pa-0
    mb-6
  >
    <v-simple-table
      v-if="multiLayout === 'table'"
      class="si-plain-table"
    >
      <thead>
        <tr v-if="label">
          <td>
            <v-label>{{ label }} <span v-if="multiMinRequired > 0"> (required {{ multiMinRequired }})</span></v-label>
          </td>
        </tr>
        <tr>
          <th
            v-for="field in filteredFields"
            :key="field.id"
          >
            {{ field.label }}
            <SiHelpIcon
              v-if="field.help"
              :help-text="field.help"
              color="info"
              icon-class="ml-2"
            />
          </th>
          <th width="60" />
        </tr>
      </thead>
      <tbody>
        <template v-if="(lazyValue || []).length === 0">
          <tr class="no-hover">
            <td
              colspan="1"
              class="grey--text font-italic"
            >
              {{ multiNoItemsText || 'no items' }}
            </td>
            <td
              :colspan="(filteredFields || []).length "
              class="px-0 py-2 text-right"
            >
              <v-btn
                text
                color="primary"
                @click="addRow()"
              >
                <v-icon add>
                  add
                </v-icon>add
              </v-btn>
            </td>
          </tr>
        </template>

        <tr
          v-for="(row, i) in lazyValue"
          :key="i"
          class="no-hover"
        >
          <template v-for="(field, fieldIndex) in rowFields(row)">
            <td
              :key="`${i}_${field.name}`"
              class="pa-3 pr-0"
              style="vertical-align: top;"
              :width="`${100 / multiColumns || 2}%`"
            >
              <component
                :is="field.inputType"
                v-bind="field"
                :ref="`row_${i}_input_${fieldIndex}`"
                v-model="lazyValue[i][field.name]"
                :label="_templateLiteralString(field.label)"
                :items="_evaluateItems(field.items)"
                :rules="_rules(field.rules, lazyValue, i)"
                :min="_min(field.min)"
                :max="_max(field.max)"
                filled
                @keydown.tab="handleTab(fieldIndex, i, $event)"
              />
            </td>
          </template>
          <td
            class="px-3 pt-4 align-top text-right"
            width="60"
          >
            <v-btn
              text
              icon
              small
              color="grey"
              tabindex="-1"
              class="mt-1"
              @click="removeRow(i)"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </td>
        </tr>
      </tbody>
    </v-simple-table>

    <v-card
      v-if="multiLayout === 'form'"
      flat
    >
      <div v-if="label">
        <v-flex pa-4>
          <v-label>{{ label }} <span v-if="multiMinRequired > 0"> (required {{ multiMinRequired }})</span></v-label>
        </v-flex>
        <v-divider />
      </div>
      <div
        v-for="(row, i) in lazyValue"
        :key="i"
      >
        <v-layout
          align-centerx
          ma-0
        >
          <v-flex
            pa-4
            pr-0
          >
            <v-flex
              v-if="multiItemText"
              pt-0
              pb-3
              class="body-2"
            >
              {{ `${multiItemText} ${i + 1}` }}
            </v-flex>
            <v-layout
              wrap
              ma-0
            >
              <v-flex
                v-for="(field, fieldIndex) in rowFields(row)"
                :key="`${i}_${field.name}`"
                class="pb-0"
                xs12
                pa-0
              >
                <SiInputWrapper
                  v-bind="field"
                  :key="field.id"
                  apply-layout
                  help-spacing
                >
                  <component
                    :is="field.inputType"
                    v-bind="field"
                    :ref="`row_${i}_input_${fieldIndex}`"
                    v-model="lazyValue[i][field.name]"
                    :label="_templateLiteralString(field.label)"
                    :items="_evaluateItems(field.items)"
                    :rules="_rules(field.rules, lazyValue, i)"
                    :min="_min(field.min)"
                    :max="_max(field.max)"
                    filled
                    @keydown.tab="handleTab(rowFields(row).length === fieldIndex + 1, i, $event)"
                  />
                </SiInputWrapper>
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex
            pa-2
            shrink
          >
            <v-btn
              text
              icon
              small
              color="grey"
              tabindex="-1"
              class="mt-1"
              @click="removeRow(i)"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </v-flex>
        </v-layout>
        <v-divider />
      </div>
      <v-layout
        v-if="(lazyValue || []).length === 0"
        ma-0
        px-2
        align-center
      >
        <v-flex
          py-4
          px-2
          class="grey--text font-italic"
        >
          {{ multiNoItemsText || 'no items' }}
        </v-flex>
        <v-btn
          text
          color="primary"
          @click="addRow()"
        >
          <v-icon add>
            add
          </v-icon>add
        </v-btn>
      </v-layout>
    </v-card>
    <!-- <v-divider></v-divider> -->
    <v-layout
      v-if="(lazyValue || []).length > 0"
      align-center
      ma-0
      mt-2
    >
      <v-fade-transition>
        <v-layout
          v-if="tabMessage"
          ma-0
          align-center
        >
          <v-icon
            class="mr-2"
            color="blue"
          >
            info
          </v-icon>
          <span class="caption blue--text"> add new lines by using the <kbd class="blue">tab</kbd> key </span>
        </v-layout>
      </v-fade-transition>

      <v-spacer />
      <v-btn
        text
        color="primary"
        @click="addRow(true)"
      >
        <v-icon add>
          add
        </v-icon>add
      </v-btn>
    </v-layout>
  </v-flex>
</template>

<script>
import conditions from '@/mixins/conditions'
import field from '@/mixins/field'
import helpers from '@/mixins/helpers'
export default {
  mixins: [ helpers, conditions, field ],
  props: {
    value: Array,
    fields: Array,
    // noItemsText: String,
    multiMinRequired: Number,
    multiInitializeWith: Number,
    multiNoItemsText: String,
    multiItemText: String,
    multiLayout: {
      type: String,
      default: 'table'
    },
    multiColumns: {
      type: Number,
      default: 2
    },
    label: String,
    cData: Object
  },
  data () {
    return {
      lazyValue: [],
      tabMessage: false,
      tabMessageShown: false
    }
  },
  computed: {
    flex () {
      var flexPercent = 100 / (this.filteredFields || []).length
      flexPercent = Math.min(flexPercent, 50)
      return `-ms-flex-preferred-size: ${flexPercent}%;
      flex-basis: ${flexPercent}%;
      -webkit-box-flex: 0;
      -ms-flex-positive: 0;
      flex-grow: 0;
      max-width: ${flexPercent}%;`
    },
    filteredFields () {
      return this.fields.filter(f => { return this._show(f.condition) }) || []
    },
    rowCellWidths () {
      if (!this.value) return []

      return this.value.map(v => Object.keys(v || {}).length)
    }
  },
  watch: {
    value: {
      handler (v) {
        this.lazyValue = v || []
      },
      deep: true
    },
    lazyValue: {
      handler (v) {
        this.$emit('input', v)
      },
      deep: true
    }
  },
  mounted () {
    let initWith = this.multiMinRequired || this.multiInitializeWith
    let value = initWith > 0 && (this.value || []).length === 0
      ? [...Array(initWith).keys()].map(this.defaultRow)
      : this.value || []
    this.lazyValue = value
  },
  methods: {
    rowFields (row) {
      return this.fields.filter(f => { return this._show(f.condition, this.cData, row) }) || []
    },
    defaultRow () {
      if (!this.filteredFields) return {}
      let o = {}
      this.filteredFields.forEach(field => {
        o[field.name] = undefined
      })
      return o
    },
    handleTab (lastInput, i, e) {
      if (e.code !== 'Tab') return
      if (lastInput && i === (this.lazyValue || []).length - 1) {
        this.addRow()
        this.tabMessage = false
      }
    },
    addRow (button) {
      let newRowIndex = (this.lazyValue || []).length
      this.$set(this.lazyValue, newRowIndex, this.defaultRow())

      setTimeout(() => {
        this.$refs[`row_${newRowIndex}_input_0`][0].focus()
      })

      if (!this.tabMessageShown && button) {
        this.tabMessage = true
        this.tabMessageShown = true
        setTimeout(() => {
          this.tabMessage = false
        }, 4000)
      }
    },
    removeRow (i) {
      let requiredItems = this.multiMinRequired || 0
      let currentItemCount = this.lazyValue.length
      let missingItems = requiredItems - (currentItemCount - 1)

      this.$delete(this.lazyValue, i)

      this.$nextTick(() => {
        if (missingItems > 0) {
          this.lazyValue = [
            ...this.lazyValue,
            ...[Array(missingItems).keys()].map(this.defaultRow)
          ]
        }
      })
    }
  }
}
</script>

<style lang="css" scoped>
</style>
